import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CloseCircle } from "iconsax-react";
import { Button, Prompt, PromptForm } from "@/shared/ui";
import { extractErrorMessage } from "@/shared/utils";
import { useRevokeSigningDocumentMutation } from "../api";

interface Props {
  documentId?: string;
  onRevoke?: () => Promise<unknown>;
  onSuccess: () => void;
  disabled?: boolean;
  size?: "md" | "lg" | "huge";
}

export default function RevokeSigningButton({ documentId, onRevoke, onSuccess, disabled = false, size = "md" }: Props) {
  const { t } = useTranslation("Common");
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revokeDocument, { isLoading: isRevokingGeneric }] = useRevokeSigningDocumentMutation();
  const [isRevokingCustom, setIsRevokingCustom] = useState(false);
  const isRevoking = isRevokingGeneric || isRevokingCustom;
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant?: "success" | "error" } | null>(null);

  async function handleRevokeConfirm() {
    if (isRevoking) return;

    try {
      if (onRevoke) {
        setIsRevokingCustom(true);
        await onRevoke();
      } else {
        await revokeDocument(documentId!).unwrap();
      }
      setConfirmRevoke(false);
      setPrompt({
        title: t("signing.revoke.successTitle"),
        text: t("signing.revoke.successText"),
        variant: "success",
      });
      onSuccess();
    } catch (err: unknown) {
      setConfirmRevoke(false);
      setPrompt({
        title: t("signing.revoke.errorTitle"),
        text: extractErrorMessage(err, "signing.revoke.errorText", t),
        variant: "error",
      });
    } finally {
      setIsRevokingCustom(false);
    }
  }

  return (
    <>
      <Button
        variant="danger"
        size={size}
        className="w-full"
        disabled={disabled || isRevoking}
        onClick={() => setConfirmRevoke(true)}>
        <CloseCircle size={16} color="currentColor" />
        {t("signing.revoke.button")}
      </Button>

      {confirmRevoke && (
        <PromptForm
          title={t("signing.revoke.confirmTitle")}
          text={t("signing.revoke.confirmText")}
          variant="error"
          onClose={() => setConfirmRevoke(false)}
          onConfirm={handleRevokeConfirm}
          isLoading={isRevoking}
          confirmText={t("signing.revoke.confirmButton")}
          cancelText={t("signing.revoke.cancelButton")}
          namespace="Common"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={() => setPrompt(null)}
          namespace="Common"
        />
      )}
    </>
  );
}
